/**
 * Clé standardisée pour le token d'authentification
 * @type {string}
 */
export const TOKEN_KEY = 'userToken';

/**
 * Définir le token d'authentification
 * @param {string} token - Token JWT à stocker
 */
export const setTokenAuth = (token) => {
    if (token) {
        localStorage.setItem(TOKEN_KEY, token);

        // Nettoyer les autres clés pour éviter la confusion
        if (TOKEN_KEY !== 'token') localStorage.removeItem('token');
        sessionStorage.removeItem(TOKEN_KEY);
        sessionStorage.removeItem('token');

        console.log('Token stored successfully under key:', TOKEN_KEY);
    } else {
        console.warn('Attempted to store empty token');
    }
};

/**
 * Récupérer le token d'authentification
 * @returns {string|null} - Token d'authentification ou null si non trouvé
 */
export const getTokenAuth = () => {
    // Priorité à la clé standardisée
    const standardToken = localStorage.getItem(TOKEN_KEY);
    if (standardToken) return standardToken;

    // Vérifier les autres clés (pour la compatibilité pendant la transition)
    const legacyToken = localStorage.getItem('token');
    const sessionStandardToken = sessionStorage.getItem(TOKEN_KEY);
    const sessionLegacyToken = sessionStorage.getItem('token');

    const alternativeToken = legacyToken || sessionStandardToken || sessionLegacyToken;

    if (alternativeToken) {
        console.warn(`Using non-standard token storage key. Migrating to ${TOKEN_KEY}.`);
        // Migrer automatiquement vers la clé standardisée
        setTokenAuth(alternativeToken);
        return alternativeToken;
    }

    return null;
};

/**
 * Récupérer la Key token d'authentification
 * @returns {string|null} - Token Key d'authentification 
 */
export const getTokenKey = () => {
    // Priorité à la clé standardisée
    return TOKEN_KEY;
};

/**
 * Supprimer le token d'authentification
 */
export const removeTokenAuth = () => {
    localStorage.removeItem(TOKEN_KEY);

    // Nettoyer aussi les autres clés possibles
    localStorage.removeItem('token');
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem('token');

    console.log('Auth token removed from all storage locations');
};

/**
 * Vérifier si un token d'authentification existe
 * @returns {boolean} - True si un token existe
 */
export const hasAuthToken = () => {
    return getTokenAuth() !== null;
};

/**
 * Vérifier si le token est expiré
 * @returns {boolean} - True si le token est expiré ou invalide
 */
export const isTokenExpired = () => {
    const token = getTokenAuth();
    if (!token) return true;

    try {
        // Décoder le payload du token (partie du milieu)
        const payload = JSON.parse(atob(token.split('.')[1]));

        // Vérifier l'expiration
        const expirationTime = payload.exp * 1000; // Convertir en millisecondes
        return Date.now() >= expirationTime;
    } catch (error) {
        console.error('Error checking token expiration:', error);
        return true; // En cas d'erreur, considérer le token comme expiré
    }
};

/**
 * Récupérer les informations de l'utilisateur depuis le token
 * @returns {Object|null} - Objet utilisateur ou null si n'existe pas
 */
export const getUserFromToken = () => {
    const token = getTokenAuth();
    if (!token) return null;

    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload;
    } catch (error) {
        console.error('Error decoding token:', error);
        return null;
    }
};

/**
 * Vérifier si l'utilisateur est admin
 * @returns {boolean} - True si l'utilisateur est admin
 */
export const isUserAdmin = () => {
    const user = getUserFromToken();
    return user && user.isAdmin === true;
};